import { useQueryClient } from "react-query";
import { useHistory, useParams } from "react-router-dom";
import { Icon } from "@/react/components/shared/Icon";
import {
  dateStringToTimeAgo,
  dateToTimeAgoFormats,
} from "@circle-react/helpers/dateTimeHelpers/timeAgo";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { useHelpWidgetContext } from "../../context/HelpWidgetContext";
import { useDeleteConversation } from "../../hooks/useDeleteConversation";
import type { ConversationStateTransition } from "../../types/ConversationStateTransition";
import type { ConversationStatus } from "../../types/ConversationStatus";
import type { HelpWidgetMessage } from "../../types/help_widget_message";
import { ConversationStatusPill } from "./ConversationStatusPill";
import { UnreadIndicator } from "./UnreadIndicator";

export interface Conversation {
  channel?: "email" | "live_chat" | "circle_bot";
  conversation_created_at: string;
  conversation_updated_at: string;
  id: string;
  is_bot_processing: boolean;
  is_waiting_in_queue: boolean;
  messages: HelpWidgetMessage[];
  queue_position: number | null;
  state_transition: ConversationStateTransition[];
  status: ConversationStatus;
  title: string;
  unread_messages_count: number;
}

export const ConversationItem = ({
  conversation,
}: {
  conversation: Conversation;
}) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { id: selectedId } = useParams<{ id: string }>();
  const isSelected = String(selectedId) === String(conversation.id);
  const { isExpanded } = useHelpWidgetContext();

  const formattedDate = conversation.conversation_updated_at
    ? dateStringToTimeAgo(conversation.conversation_updated_at, {
        format: dateToTimeAgoFormats.short,
      })
    : "";

  const handleClick = () => {
    if (conversation.channel === "email" && conversation.status === "draft") {
      history.push(`/conversations/${conversation.id}/new_ticket`);
    } else {
      history.push(`/conversations/${conversation.id}`);
    }
  };

  const { mutate: deleteConversation } = useDeleteConversation({
    onSuccess: () => {
      void queryClient.invalidateQueries(["help-widget", "conversations"]);
    },
    onError: error => {
      console.error("Error deleting conversation:", error);
    },
  });

  return (
    <button
      className={classNames(
        "hover:bg-secondary line-clamp-2 w-full rounded-lg p-2 text-left transition-colors duration-200",
        {
          "bg-tertiary": isSelected,
        },
      )}
      onClick={handleClick}
      type="button"
    >
      <div className="flex flex-col py-[3px]">
        <div className="flex justify-between gap-2">
          <div className="flex items-center gap-2">
            <Typography.LabelSm color="text-darkest" weight="medium">
              {conversation.title}
            </Typography.LabelSm>
            <UnreadIndicator count={conversation.unread_messages_count} />
          </div>
          <div className="flex items-center">
            {!isExpanded && (
              <>
                <ConversationStatusPill status={conversation.status} />
                {conversation.status === "draft" && (
                  <button
                    onClick={event => {
                      event.stopPropagation();
                      deleteConversation({ conversationId: conversation.id });
                    }}
                    type="button"
                    className="bg-primary border-primary dark:text-feature-light flex h-6 w-6 items-center justify-center rounded-full border transition-colors"
                  >
                    <Icon
                      type="16-trash-bin-2"
                      size={16}
                      useWithFillCurrentColor
                    />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 truncate">
          <Typography.LabelSm color="text-tertiary" weight="medium">
            {conversation.state_transition.join(" → ")}
          </Typography.LabelSm>
          <div className="bg-tertiary h-1 w-1 rounded-full" />
          <Typography.LabelSm color="text-tertiary" weight="medium">
            {formattedDate}
          </Typography.LabelSm>
        </div>
      </div>
    </button>
  );
};
